import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  useMediaQuery,
  Skeleton,
  LinearProgress
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import IronIcon from '@mui/icons-material/Iron';
import VerifiedIcon from '@mui/icons-material/Verified';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import BuildIcon from '@mui/icons-material/Build';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { usePermissions } from '../hooks/usePermissions';

interface DashboardStats {
  period: string;
  start_date: string;
  end_date: string;
  today: {
    stitched: number;
    pressed: number;
    barcoded: number;
    dispatched: number;
  };
  pipeline: {
    cutting: number;
    stitching: number;
    dhaga: number;
    press: number;
    qc: number;
    barcoded: number;
    repair: number;
    damaged: number;
  };
  total_active_workers: number;
  pending_designs: number;
  press_pieces: number;
  stitching_pieces: number;
  monthly_job_amount: number | null;
  financial: {
    worker_payments: number;
    expenses: number;
    purchases: number;
  };
  qc_pending_designs_count: number;
  qc_pending_pieces_total: number;
  repair_pending_items_count: number;
  repair_pending_pieces_total: number;
  worker_summary: {
    worker_name: string;
    job_amount: number;
    stitched_pieces: number;
    press_pieces: number;
  }[];
  pending_job_works: {
    id: string;
    jobWorkCode: string;
    designNo: string;
    vendor_name: string;
    job_type: string;
    design_image: string;
    issueDate: string;
    days_since_issued: number;
    product_type: string;
    total_qty_given: number;
    total_qty_received: number;
    pending_qty: number;
  }[];
  job_work_ageing: {
    '0_15': { count: number; pieces: number };
    '15_30': { count: number; pieces: number };
    '30_60': { count: number; pieces: number };
    '60_plus': { count: number; pieces: number };
  };
  completed_designs: number;
  total_barcoded_all: number;
}

// --- Reusable Components ---

const StatCard = ({ title, value, subtitle, icon, color, onClick }: {
  title: string; value: string | number; subtitle?: string;
  icon: React.ReactNode; color: string; onClick?: () => void;
}) => (
  <Paper
    onClick={onClick}
    sx={{
      p: { xs: 2, sm: 2.5 },
      borderRadius: 3,
      background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.02)} 100%)`,
      border: `1px solid ${alpha(color, 0.15)}`,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s ease',
      '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: `0 4px 20px ${alpha(color, 0.15)}` } : {},
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(color, 0.12), color: color, flexShrink: 0
      }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography variant="h5" fontWeight={700} sx={{ color, lineHeight: 1.2 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  </Paper>
);

const PipelineStage = ({ label, value, color, total, icon, subtitle }: {
  label: string; value: number; color: string; total: number; icon: React.ReactNode; subtitle?: string;
}) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <Box sx={{ textAlign: 'center', flex: 1, minWidth: 70 }}>
      <Box sx={{
        width: 48, height: 48, borderRadius: '50%', mx: 'auto', mb: 0.5,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(color, 0.12), color: color, border: `2px solid ${alpha(color, 0.3)}`
      }}>
        {icon}
      </Box>
      <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.1, color }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', lineHeight: 1.2 }}>{label}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block', lineHeight: 1, opacity: 0.7 }}>{subtitle}</Typography>
      )}
      <LinearProgress
        variant="determinate" value={pct}
        sx={{
          mt: 0.5, height: 3, borderRadius: 2, bgcolor: alpha(color, 0.1),
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 2 }
        }}
      />
    </Box>
  );
};

const AgeingChip = ({ label, count, pieces, color }: {
  label: string; count: number; pieces: number; color: string;
}) => (
  <Paper sx={{
    p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, textAlign: 'center', flex: 1, minWidth: 80,
    border: `1px solid ${alpha(color, 0.2)}`,
    background: `linear-gradient(135deg, ${alpha(color, 0.06)} 0%, transparent 100%)`
  }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>{label}</Typography>
    <Typography variant="h5" fontWeight={700} sx={{ color, lineHeight: 1.2 }}>{count}</Typography>
    <Typography variant="caption" color="text.secondary">{pieces} pcs</Typography>
  </Paper>
);

// --- Main Dashboard ---

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');
  
  const { canView } = usePermissions();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const canViewStatistics = canView('dashboard-statistics');
  const canViewPendingJobs = canView('dashboard-pending-jobs');
  const canViewDashboard = canView('dashboard');
  const hasAnyDashboardPermission = canViewDashboard || canViewStatistics || canViewPendingJobs;

  const fetchStats = async (period: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/dashboard/stats?period=${period}`);
      setStats(response.data);
    } catch (error: any) {
      console.error('Error fetching dashboard stats:', error);
      setError(error.response?.data?.error || 'Failed to fetch dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (event: SelectChangeEvent) => {
    const newPeriod = event.target.value;
    setSelectedPeriod(newPeriod);
    fetchStats(newPeriod);
  };

  useEffect(() => {
    if (hasAnyDashboardPermission) fetchStats(selectedPeriod);
  }, [hasAnyDashboardPermission]);

  if (!hasAnyDashboardPermission) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Typography variant="h3" fontWeight="bold" color="primary" textAlign="center">Welcome to Stitchflo</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Grid container spacing={2}>
          {[...Array(8)].map((_, i) => (
            <Grid item xs={6} sm={3} key={i}><Skeleton variant="rounded" height={90} sx={{ borderRadius: 3 }} /></Grid>
          ))}
          <Grid item xs={12}><Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} /></Grid>
          <Grid item xs={12}><Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} /></Grid>
        </Grid>
      </Box>
    );
  }

  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!stats) return null;

  const formatCurrency = (v: number | null | undefined) => v ? `₹${v.toLocaleString('en-IN')}` : '₹0';
  const formatPeriodLabel = (p: string) => {
    switch (p) { case 'current_month': return 'Current Month'; case 'previous_month': return 'Previous Month'; case 'financial_year': return 'Financial Year'; default: return 'Current Month'; }
  };

  const pipelineTotal = stats.pipeline.cutting + stats.pipeline.stitching + stats.pipeline.dhaga +
    stats.pipeline.press + stats.pipeline.qc + stats.pipeline.barcoded + stats.pipeline.repair;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>Dashboard</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatPeriodLabel(stats.period)} · {new Date(stats.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} – {new Date(stats.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Period</InputLabel>
          <Select value={selectedPeriod} label="Period" onChange={handlePeriodChange}>
            <MenuItem value="current_month">Current Month</MenuItem>
            <MenuItem value="previous_month">Previous Month</MenuItem>
            <MenuItem value="financial_year">Financial Year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {canViewStatistics && (
        <>
          {/* ===== TODAY'S ACTIVITY ===== */}
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block', letterSpacing: 1.5, fontWeight: 600 }}>
            Today's Activity
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4}>
              <StatCard title="Stitched" value={stats.today.stitched} subtitle="pieces today" icon={<CheckroomIcon fontSize="small" />} color="#7c3aed" />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard title="Pressed" value={stats.today.pressed} subtitle="pieces today" icon={<IronIcon fontSize="small" />} color="#0891b2" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard title="Barcoded" value={stats.today.barcoded} subtitle="pieces today" icon={<QrCode2Icon fontSize="small" />} color="#059669" />
            </Grid>
          </Grid>

          {/* ===== PRODUCTION PIPELINE ===== */}
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block', letterSpacing: 1.5, fontWeight: 600 }}>
            Production Pipeline
          </Typography>
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, overflowX: 'auto', pb: 1, justifyContent: { sm: 'center' } }}>
              <PipelineStage label="Cutting" value={stats.pipeline.cutting} color="#8b5cf6" total={pipelineTotal} icon={<ContentCutIcon sx={{ fontSize: 20 }} />} subtitle="designs" />
              <Typography sx={{ display: 'flex', alignItems: 'center', color: 'divider', fontSize: 20 }}>›</Typography>
              <PipelineStage label="Stitching" value={stats.pipeline.stitching} color="#7c3aed" total={pipelineTotal} icon={<CheckroomIcon sx={{ fontSize: 20 }} />} subtitle="designs" />
              <Typography sx={{ display: 'flex', alignItems: 'center', color: 'divider', fontSize: 20 }}>›</Typography>
              <PipelineStage label="Press" value={stats.pipeline.press} color="#0891b2" total={pipelineTotal} icon={<IronIcon sx={{ fontSize: 20 }} />} subtitle="designs" />
              <Typography sx={{ display: 'flex', alignItems: 'center', color: 'divider', fontSize: 20 }}>›</Typography>
              <PipelineStage label="QC" value={stats.pipeline.qc} color="#eab308" total={pipelineTotal} icon={<VerifiedIcon sx={{ fontSize: 20 }} />} subtitle="pieces" />
              <Typography sx={{ display: 'flex', alignItems: 'center', color: 'divider', fontSize: 20 }}>›</Typography>
              <PipelineStage label="Barcoded" value={stats.pipeline.barcoded} color="#059669" total={pipelineTotal} icon={<QrCode2Icon sx={{ fontSize: 20 }} />} subtitle="this month" />
              <Typography sx={{ display: 'flex', alignItems: 'center', color: 'divider', fontSize: 20 }}>|</Typography>
              <PipelineStage label="Repair" value={stats.pipeline.repair} color="#ef4444" total={pipelineTotal} icon={<BuildIcon sx={{ fontSize: 20 }} />} subtitle="pieces" />
            </Box>
              </Paper>

          {/* ===== PERIOD STATS + FINANCIAL ===== */}
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="Stitching" value={stats.stitching_pieces} subtitle={formatPeriodLabel(stats.period)} icon={<CheckroomIcon fontSize="small" />} color="#7c3aed" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="Press" value={stats.press_pieces} subtitle={formatPeriodLabel(stats.period)} icon={<IronIcon fontSize="small" />} color="#0891b2" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="QC Pending" value={stats.qc_pending_pieces_total} subtitle={`${stats.qc_pending_designs_count} designs`} icon={<VerifiedIcon fontSize="small" />} color="#eab308"
                onClick={() => navigate('/production/qc')} />
                </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="In Repair" value={stats.repair_pending_pieces_total} subtitle={`${stats.repair_pending_items_count} items`} icon={<BuildIcon fontSize="small" />} color="#ef4444"
                onClick={() => navigate('/production/repairs')} />
                </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="Workers" value={stats.total_active_workers} subtitle="active" icon={<PeopleIcon fontSize="small" />} color="#6366f1" />
                </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="Designs" value={stats.pending_designs} subtitle="in process" icon={<TrendingUpIcon fontSize="small" />} color="#f59e0b" />
                </Grid>
              </Grid>

          {/* ===== FINANCIAL SUMMARY ===== */}
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block', letterSpacing: 1.5, fontWeight: 600 }}>
            Financial Summary · {formatPeriodLabel(stats.period)}
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4}>
              <StatCard title="Worker Payments" value={formatCurrency(stats.financial.worker_payments)} icon={<AccountBalanceWalletIcon fontSize="small" />} color="#7c3aed" />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard title="Expenses" value={formatCurrency(stats.financial.expenses)} icon={<ReceiptLongIcon fontSize="small" />} color="#ef4444"
                onClick={() => navigate('/expenses')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard title="Purchases" value={formatCurrency(stats.financial.purchases)} icon={<ShoppingCartIcon fontSize="small" />} color="#0891b2"
                onClick={() => navigate('/purchases')} />
            </Grid>
          </Grid>

          {/* ===== JOB WORK AGEING ===== */}
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block', letterSpacing: 1.5, fontWeight: 600 }}>
            Job Work Ageing
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 3, overflowX: 'auto' }}>
            <AgeingChip label="0–15 days" count={stats.job_work_ageing['0_15'].count} pieces={stats.job_work_ageing['0_15'].pieces} color="#059669" />
            <AgeingChip label="15–30 days" count={stats.job_work_ageing['15_30'].count} pieces={stats.job_work_ageing['15_30'].pieces} color="#eab308" />
            <AgeingChip label="30–60 days" count={stats.job_work_ageing['30_60'].count} pieces={stats.job_work_ageing['30_60'].pieces} color="#f97316" />
            <AgeingChip label="60+ days" count={stats.job_work_ageing['60_plus'].count} pieces={stats.job_work_ageing['60_plus'].pieces} color="#ef4444" />
          </Box>
          </>
        )}

      {/* ===== VENDOR-WISE PENDING JOB WORKS ===== */}
      {canViewPendingJobs && stats.pending_job_works.length > 0 && (() => {
        // Aggregate pending job works by vendor
        const vendorMap = new Map<string, { pending_jobs: number; pending_pieces: number; oldest_days: number; job_types: Set<string> }>();
        stats.pending_job_works.forEach(jw => {
          const existing = vendorMap.get(jw.vendor_name) || { pending_jobs: 0, pending_pieces: 0, oldest_days: 0, job_types: new Set<string>() };
          existing.pending_jobs++;
          existing.pending_pieces += Math.max(0, jw.pending_qty);
          existing.oldest_days = Math.max(existing.oldest_days, jw.days_since_issued);
          existing.job_types.add(jw.job_type);
          vendorMap.set(jw.vendor_name, existing);
        });
        const vendorSummary = Array.from(vendorMap.entries())
          .map(([name, data]) => ({ name, ...data, job_types_str: Array.from(data.job_types).join(', ') }))
          .sort((a, b) => b.oldest_days - a.oldest_days);

        return (
          <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, mb: 3, borderRadius: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
              Vendor-wise Pending Summary
            </Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                    <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Vendor</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Pending Jobs</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Pending Pcs</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Job Types</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Oldest Delay</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                  {vendorSummary.map(v => (
                    <TableRow key={v.name} hover sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/job-works?vendor=${encodeURIComponent(v.name)}`)}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{v.name}</TableCell>
                      <TableCell align="center">{v.pending_jobs}</TableCell>
                      <TableCell align="center">
                        <Typography fontWeight={600} color={v.pending_pieces > 0 ? 'primary.main' : 'text.secondary'}>
                          {v.pending_pieces}
                            </Typography>
                          </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{v.job_types_str}</Typography>
                          </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${v.oldest_days}d`} size="small"
                          color={v.oldest_days > 60 ? 'error' : v.oldest_days > 30 ? 'warning' : 'default'}
                          variant={v.oldest_days > 30 ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 600, minWidth: 50 }}
                        />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
        );
      })()}

    </Box>
  );
} 
